import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Department, Organization, ID } from '@shared/types';
import type { Timestamp } from 'firebase/firestore';
import { writeAuditLog } from '@shared/utils';

// ─── 組織スキーマ ───────────────────────────────────────────────────────────

export const organizationCreateSchema = z.object({
  name: z.string().min(1, '組織名を入力してください'),
  plan: z.enum(['standard', 'enterprise']),
  maxUsers: z.number().int().min(1).max(1000),
});

export type OrganizationCreateInput = z.infer<typeof organizationCreateSchema>;

// ─── 組織 CRUD ──────────────────────────────────────────────────────────────

export async function fetchAllOrganizations(): Promise<Organization[]> {
  try {
    const ref = collection(db, 'organizations');
    const q = query(ref, where('isDeleted', '==', false), orderBy('name'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Organization);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`組織一覧の取得に失敗しました: ${msg}`);
  }
}

export async function createOrganization(
  data: OrganizationCreateInput,
  userId: ID
): Promise<Organization> {
  try {
    const validated = parseOrThrow(organizationCreateSchema, data);
    const now = serverTimestamp() as unknown as Timestamp;
    const payload: Omit<Organization, 'id'> = {
      ...validated,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
      isDeleted: false,
    };
    const ref = await addDoc(collection(db, 'organizations'), payload);
    await writeAuditLog({ entityType: 'organization', entityId: ref.id, action: 'create', userId, after: validated });
    return { id: ref.id, ...payload };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`組織の作成に失敗しました: ${msg}`);
  }
}

export const departmentCreateSchema = z.object({
  name: z.string().min(1, '部署名を入力してください'),
  organizationId: z.string().min(1, '組織IDが必要です'),
  parentId: z.string().nullable().default(null),
  managerId: z.string().nullable().default(null),
});

export type DepartmentCreateInput = z.infer<typeof departmentCreateSchema>;

export async function fetchOrganization(id: ID): Promise<Organization> {
  try {
    const ref = doc(db, 'organizations', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('組織が見つかりません');
    return { id: snap.id, ...snap.data() } as Organization;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`組織情報の取得に失敗しました: ${msg}`);
  }
}

export async function fetchDepartments(): Promise<Department[]> {
  try {
    const ref = collection(db, 'departments');
    const q = query(ref, where('isDeleted', '==', false), orderBy('name'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Department);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`部署一覧の取得に失敗しました: ${msg}`);
  }
}

export async function createDepartment(
  data: DepartmentCreateInput,
  userId: ID
): Promise<Department> {
  try {
    const validated = parseOrThrow(departmentCreateSchema, data);
    const now = serverTimestamp() as unknown as Timestamp;
    const payload: Omit<Department, 'id'> = {
      ...validated,
      parentId: validated.parentId ?? null,
      managerId: validated.managerId ?? null,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
      isDeleted: false,
    };
    const ref = await addDoc(collection(db, 'departments'), payload);
    await writeAuditLog({ entityType: 'department', entityId: ref.id, action: 'create', userId, after: validated });
    return { id: ref.id, ...payload };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`部署の作成に失敗しました: ${msg}`);
  }
}

export async function updateDepartment(
  id: ID,
  data: Partial<DepartmentCreateInput>,
  userId: ID
): Promise<Department> {
  try {
    const ref = doc(db, 'departments', id);
    await updateDoc(ref, {
      ...data,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
    const snap = await getDoc(ref);
    await writeAuditLog({ entityType: 'department', entityId: id, action: 'update', userId, after: data });
    return { id: snap.id, ...snap.data() } as Department;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`部署の更新に失敗しました: ${msg}`);
  }
}

export async function deleteDepartment(id: ID, userId: ID): Promise<void> {
  try {
    const ref = doc(db, 'departments', id);
    await updateDoc(ref, {
      isDeleted: true,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
    await writeAuditLog({ entityType: 'department', entityId: id, action: 'delete', userId, after: { isDeleted: true } });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`部署の削除に失敗しました: ${msg}`);
  }
}
